/**
 * API Client
 * Fetch API를 사용한 HTTP 클라이언트
 */

import API_CONFIG from '../config/api.config';
import type { APIResponse, ErrorResponse } from './types';
import { log, LogCategory } from '../lib/logger';
import { getAccessToken } from '../../utils/auth';

function getAuthHeaders(): Record<string, string> {
  const token = getAccessToken();
  if (token) return { Authorization: `Bearer ${token}` };
  return {};
}

export class APIError extends Error {
  constructor(
    public status: number,
    public message: string,
    public response?: ErrorResponse
  ) {
    super(message);
    this.name = 'APIError';
  }
}

interface RequestOptions extends RequestInit {
  timeout?: number;
  retry?: number;
}

class APIClient {
  private baseURL: string;
  private defaultTimeout: number;
  private maxRetries: number;

  constructor() {
    this.baseURL = API_CONFIG.API_URL;
    this.defaultTimeout = API_CONFIG.TIMEOUT;
    this.maxRetries = API_CONFIG.RETRY.MAX_ATTEMPTS;
  }

  /**
   * Fetch with timeout
   */
  private async fetchWithTimeout(
    url: string,
    options: RequestOptions = {}
  ): Promise<Response> {
    const { timeout = this.defaultTimeout, ...fetchOptions } = options;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new APIError(408, 'Request timeout');
      }
      throw error;
    }
  }

  /**
   * Retry logic
   */
  private async fetchWithRetry(
    url: string,
    options: RequestOptions = {}
  ): Promise<Response> {
    const { retry = this.maxRetries } = options;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < retry; attempt++) {
      try {
        return await this.fetchWithTimeout(url, options);
      } catch (error) {
        lastError = error as Error;

        // Don't retry on 4xx errors
        if (error instanceof APIError && error.status >= 400 && error.status < 500) {
          throw error;
        }

        // Wait before retry (exponential backoff)
        if (attempt < retry - 1) {
          await new Promise(resolve =>
            setTimeout(resolve, API_CONFIG.RETRY.DELAY * Math.pow(2, attempt))
          );
        }
      }
    }

    throw lastError;
  }

  /**
   * Parse response
   */
  private async parseResponse<T>(response: Response): Promise<APIResponse<T>> {
    const contentType = response.headers.get('content-type');

    if (!contentType?.includes('application/json')) {
      throw new APIError(
        response.status,
        'Invalid response format',
        { success: false, message: 'Expected JSON response' }
      );
    }

    const data = await response.json();

    if (!response.ok) {
      throw new APIError(response.status, data.message || 'Request failed', data);
    }

    return data as APIResponse<T>;
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, options: RequestOptions = {}): Promise<APIResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    const response = await this.fetchWithRetry(url, {
      ...options,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
        ...options.headers,
      },
    });

    return this.parseResponse<T>(response);
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    body?: unknown,
    options: RequestOptions = {}
  ): Promise<APIResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    // Check if body is FormData
    const isFormData = body instanceof FormData;

    // Debug logging
    log.debug(LogCategory.API, 'API Client POST', {
      endpoint,
      url,
      isFormData,
      bodyType: body?.constructor?.name,
      hasBody: !!body
    });

    if (isFormData && body instanceof FormData) {
      log.debug(LogCategory.API, 'FormData entries:');
      for (const [key, value] of (body as FormData).entries()) {
        log.debug(LogCategory.API, `FormData field ${key}: ${typeof value === 'string' ? value.length + ' chars' : value}`);
      }
    }

    const requestConfig = {
      ...options,
      method: 'POST',
      headers: isFormData
        ? {
            ...getAuthHeaders(),
            ...options.headers,
          }
        : {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
            ...options.headers,
          },
      body: body
        ? isFormData
          ? (body as FormData) // Send FormData as-is
          : JSON.stringify(body) // JSON stringify for regular objects
        : undefined,
    };

    log.debug(LogCategory.API, 'Request headers:', requestConfig.headers);
    log.debug(LogCategory.API, 'Body type:', requestConfig.body?.constructor?.name);

    const response = await this.fetchWithRetry(url, requestConfig);

    log.info(LogCategory.API, 'API Client POST Response', { status: response.status, statusText: response.statusText });
    log.debug(LogCategory.API, 'Response Content-Type', { contentType: response.headers.get('content-type') });

    return this.parseResponse<T>(response);
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    body?: unknown,
    options: RequestOptions = {}
  ): Promise<APIResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    // Check if body is FormData
    const isFormData = body instanceof FormData;

    // Debug logging
    log.debug(LogCategory.API, 'API Client PUT', {
      endpoint,
      url,
      isFormData,
      bodyType: body?.constructor?.name,
      hasBody: !!body
    });

    if (isFormData && body instanceof FormData) {
      log.debug(LogCategory.API, 'FormData entries:');
      for (const [key, value] of (body as FormData).entries()) {
        log.debug(LogCategory.API, `FormData field ${key}: ${typeof value === 'string' ? value.length + ' chars' : value}`);
      }
    }

    const requestConfig = {
      ...options,
      method: 'PUT',
      headers: isFormData
        ? {
            ...getAuthHeaders(),
            ...options.headers,
          }
        : {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
            ...options.headers,
          },
      body: body
        ? isFormData
          ? (body as FormData) // Send FormData as-is
          : JSON.stringify(body) // JSON stringify for regular objects
        : undefined,
    };

    log.debug(LogCategory.API, 'Request headers:', requestConfig.headers);
    log.debug(LogCategory.API, 'Body type:', requestConfig.body?.constructor?.name);

    const response = await this.fetchWithRetry(url, requestConfig);

    log.info(LogCategory.API, 'API Client PUT Response', { status: response.status, statusText: response.statusText });
    log.debug(LogCategory.API, 'Response Content-Type', { contentType: response.headers.get('content-type') });

    return this.parseResponse<T>(response);
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options: RequestOptions = {}): Promise<APIResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    const response = await this.fetchWithRetry(url, {
      ...options,
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
        ...options.headers,
      },
    });

    return this.parseResponse<T>(response);
  }
}

// Singleton instance
export const apiClient = new APIClient();
export default apiClient;
